import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useUserPermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldX, ArrowLeft, User, Building2 } from "lucide-react";

export function UnauthorizedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { localUser } = useAuthStore();
  const userPermissions = useUserPermissions();

  const handleGoBack = () => {
    navigate("/admin");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
            <ShieldX className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Access Denied
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            You don't have permission to access this page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {/* User Info */}
            <div className="flex items-center space-x-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Role:</span>
              <Badge variant="secondary">
                {localUser?.role?.name.replace("_", " ").toUpperCase() ||
                  "Not assigned"}
              </Badge>
            </div>

            {/* Company Info */}
            {localUser?.company && (
              <div className="flex items-center space-x-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Company:</span>
                <span className="text-muted-foreground">
                  {localUser.company.name}
                </span>
              </div>
            )}

            {/* Current Path */}
            <div className="text-sm">
              <span className="font-medium">Attempted path:</span>
              <code className="ml-2 px-2 py-1 bg-muted rounded text-xs">
                {location.pathname}
              </code>
            </div>

            {/* User Permissions */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Your permissions:</span>
              <div className="flex flex-wrap gap-1">
                {userPermissions.length > 0 ? (
                  userPermissions.map((permission) => (
                    <Badge
                      key={permission}
                      variant="outline"
                      className="text-xs"
                    >
                      {permission.replace("_", " ")}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">
                    No permissions assigned
                  </span>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              If you believe this is an error, please contact your
              administrator.
            </p>
          </div>

          <Button onClick={handleGoBack} className="w-full" variant="default">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default UnauthorizedPage;
